import { CancelledError } from '@tanstack/react-query';
import { type FetchLike, type Middleware, type WretchOptions } from 'wretch';
import { FetchError, ParseError } from '@/api/errors';

/**
 * Guard middleware to catch fetch errors, HTTP errors (non-ok responses) and JSON parse errors, and convert them into our custom error classes.
 *
 * Important: This middleware should be the last one in the chain, so that it can catch all errors thrown by other middlewares or the fetch itself.
 */
export const guardMiddleware: Middleware = () => (next: FetchLike) => (url: string, opts: WretchOptions) =>
  next(url, opts)
    .catch((err: unknown) => {
      if (err instanceof CancelledError) {
        // QueryClient cancellation errors are thrown as CancelledError by default, but we want to convert them into AbortError
        // This allows us to use the same error handling logic for both query cancellations and fetch abortions.
        throw new DOMException('The operation was aborted.', 'AbortError');
      }
      throw err;
    })
    .then(async (response: Response) => {
      if (!response.ok) {
        // Try to parse the error body safely
        let errorBody;
        try {
          // Clone response to avoid consuming the body if we need it later
          errorBody = await response.clone().json();
        } catch {
          errorBody = await response.clone().text();
        }

        // `response.status` can be any HTTP number, but `FetchError` types it as `StatusCodes`.
        // oxlint-disable-next-line typescript/no-unsafe-enum-assignment
        throw new FetchError(response.status, response.statusText, response.url, errorBody);
      }

      // 3. Handle Parse Errors (Wrap the response methods)
      // We wrap the original .json() method to catch SyntaxErrors
      const originalJson = response.json.bind(response);
      response.json = async () => {
        try {
          return await originalJson();
        } catch (err) {
          throw new ParseError(err instanceof Error ? err : new Error(String(err)));
        }
      };

      return response;
    });
